import React from 'react';
import { Calendar, Briefcase } from 'lucide-react';
import { normalizeDesignations } from '../utils/userProfileForm';

function Cred({ label, value }) {
  return (
    <div className="flex flex-col py-1 border-b border-zinc-50 last:border-0">
      <dt className="text-[10px] uppercase tracking-wide text-zinc-400">{label}</dt>
      <dd className="text-zinc-800 font-medium">{value || '—'}</dd>
    </div>
  );
}

function DesignationTimeline({ user }) {
  const list = normalizeDesignations(user);
  if (!list.length) {
    return <p className="text-sm text-zinc-500">No designations added yet.</p>;
  }
  return (
    <ul className="space-y-3" data-testid="designation-timeline">
      {list.map((d, i) => {
        const period = [
          d.from_date || null,
          d.is_current ? 'Present' : (d.to_date || null),
        ].filter(Boolean).join(' – ') || (d.is_current ? 'Current' : 'Past');
        return (
          <li key={`${d.title}-${i}`} className="flex gap-3 text-sm">
            <span
              className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${d.is_current ? 'bg-[#F15A22]' : 'bg-zinc-300'}`}
            />
            <div className="min-w-0">
              <p className="font-semibold text-zinc-900">
                {d.title}
                {d.is_current ? (
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-[#F15A22] font-bold">
                    Current
                  </span>
                ) : null}
              </p>
              {d.organization ? <p className="text-zinc-600">{d.organization}</p> : null}
              <p className="text-xs text-zinc-500 mt-0.5">{period}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** About + designations + clinical + schedule — grouped by section. */
export default function StaffProfileSections({ user, summary }) {
  const shifts = summary?.upcoming_shifts || [];
  const fee = user?.consultation_fee;
  return (
    <div className="space-y-4" data-testid="staff-profile-sections">
      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-zinc-900 mb-2">About</h3>
        <p className="text-sm text-zinc-600 leading-relaxed whitespace-pre-wrap">
          {user?.bio?.trim() || 'No biography added yet.'}
        </p>
      </section>

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-zinc-900 mb-3 flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-zinc-400" /> Designations
        </h3>
        <DesignationTimeline user={user} />
      </section>

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-zinc-900 mb-3">Clinical profile</h3>
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Cred label="Specialisation" value={user?.specialization} />
          <Cred label="Secondary specialisation" value={user?.secondary_specialization} />
          <Cred label="Qualifications" value={user?.qualifications} />
          <Cred label="Fellowship" value={user?.fellowship} />
          <Cred label="Registration" value={user?.registration_number} />
          <Cred label="Medical council" value={user?.medical_council} />
          <Cred label="Department" value={user?.department} />
          <Cred label="Team / category" value={user?.category} />
          <Cred label="OPD days / hours" value={user?.opd_days} />
          <Cred label="Consultation fee" value={fee != null && fee !== '' ? `₹${fee}` : null} />
          <Cred label="Languages" value={user?.languages} />
          <Cred
            label="Experience"
            value={user?.experience_years != null && user.experience_years !== ''
              ? `${user.experience_years} years` : null}
          />
          <Cred label="Clinical interests" value={user?.clinical_interests} />
          <Cred label="Phone" value={user?.phone} />
          <Cred label="Email" value={user?.email} />
        </dl>
      </section>

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-zinc-900 mb-3 flex items-center gap-2">
          <Calendar className="h-4 w-4 text-zinc-400" /> Upcoming schedule
        </h3>
        {shifts.length === 0 ? (
          <p className="text-sm text-zinc-500">No upcoming roster shifts.</p>
        ) : (
          <ul className="divide-y" data-testid="staff-upcoming-schedule">
            {shifts.map((sh, i) => (
              <li key={`${sh.date}-${sh.start}-${i}`} className="py-2.5 flex items-start justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-zinc-800">{sh.date}</p>
                  {sh.location ? (
                    <p className="text-[11px] text-zinc-500 mt-0.5 truncate">{sh.location}</p>
                  ) : null}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-zinc-700 tabular-nums font-medium">
                    {(sh.start || '—')} – {(sh.end || '—')}
                  </p>
                  <p className={`text-[10px] mt-0.5 font-semibold uppercase tracking-wide ${
                    sh.published ? 'text-emerald-700' : 'text-amber-700'
                  }`}>
                    {sh.published ? 'Published' : 'Draft'}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {(user?.booking || user?.privacy) && (
        <section className="rounded-xl border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-zinc-900 mb-2">Portal & booking</h3>
          <ul className="text-sm text-zinc-600 space-y-1">
            <li>
              Accept appointments:{' '}
              <strong>{user?.booking?.accept_appointments === false ? 'No' : 'Yes'}</strong>
            </li>
            <li>
              Patient portal booking:{' '}
              <strong>{user?.booking?.patient_portal_booking === false ? 'No' : 'Yes'}</strong>
            </li>
            <li>
              Can provide referral:{' '}
              <strong>{user?.booking?.can_provide_referral === true ? 'Yes' : 'No'}</strong>
            </li>
            <li>
              Shown on portal:{' '}
              <strong>{user?.privacy?.show_on_portal === false ? 'No' : 'Yes'}</strong>
            </li>
          </ul>
        </section>
      )}
    </div>
  );
}
